import type { CountPresentation, CountPreview, CountThreshold, CreateCountInput } from '../types/inventoryCount';
import { MAX_WASTE_QUANTITY, looksLikeThousandsSeparator, parseDecimalInput } from './wasteCapture';
import { formatWasteQuantity } from './wasteReview';

// F3 · lógica PURA de «Contar» (sin renderizador, con pruebas). Nadie resta: se escribe lo contado,
// por piezas sueltas y/o por empaques («3 cajas + 18 pz»), y la pantalla solo SUMA para mandar el
// total en la unidad base. La diferencia la calcula el servidor.

export interface CountPackageLine {
  /** Llave estable para la lista (no viaja al servidor). */
  id?: string;
  presentationId: string;
  /** Cuántos empaques se contaron, tal como se escribió. */
  packages: string;
}

export interface CountDraft {
  /** Lo suelto, en la unidad base, tal como se escribió («18», «0,5»). */
  loose: string;
  lines: CountPackageLine[];
}

export const emptyCountDraft = (): CountDraft => ({ loose: '', lines: [] });

const round4 = (value: number) => Math.round(value * 10_000) / 10_000;

/** Número escrito por una persona para CONTAR: igual que la merma, pero acepta 0. */
function readCountNumber(text: string): { value: number | null; problem: string | null } {
  const trimmed = text.trim();
  if (!trimmed) return { value: 0, problem: null };
  if (looksLikeThousandsSeparator(trimmed)) return { value: null, problem: 'Escríbelo sin separador de miles: 1000, no 1,000 (para decimales usa 1.5).' };
  const value = parseDecimalInput(trimmed, 4);
  if (value === null) return { value: null, problem: 'Escribe solo números (hasta 4 decimales).' };
  return { value, problem: null };
}

/**
 * Total contado en la unidad base: suelto + empaques × contenido. `problem` explica qué corregir;
 * `isEmpty` = todavía no se escribió nada (distinto de contar 0 a propósito).
 */
export function countTotal(draft: CountDraft, presentations: CountPresentation[]): { total: number | null; problem: string | null; isEmpty: boolean } {
  const isEmpty = !draft.loose.trim() && draft.lines.every((line) => !line.packages.trim());
  if (isEmpty) return { total: null, problem: 'Escribe cuánto contaste.', isEmpty: true };
  const loose = readCountNumber(draft.loose);
  if (loose.problem) return { total: null, problem: loose.problem, isEmpty: false };
  let total = loose.value ?? 0;
  for (const line of draft.lines) {
    if (!line.packages.trim()) continue;
    const presentation = presentations.find((option) => option.id === line.presentationId);
    if (!presentation) return { total: null, problem: 'Elige de nuevo el empaque: ya no está en la lista.', isEmpty: false };
    const packages = readCountNumber(line.packages);
    if (packages.problem) return { total: null, problem: packages.problem, isEmpty: false };
    total += (packages.value ?? 0) * Number(presentation.quantityInBaseUnit);
  }
  total = round4(total);
  if (total > MAX_WASTE_QUANTITY) return { total: null, problem: 'La cantidad es demasiado grande.', isEmpty: false };
  return { total, problem: null, isEmpty: false };
}

/** «3 × Caja de 100 + 18 pz = 318 pz»: la suma a la vista, para que nadie tenga que hacerla. */
export function countSumEcho(draft: CountDraft, presentations: CountPresentation[], unitSymbol: string): string | null {
  const parts: string[] = [];
  for (const line of draft.lines) {
    const presentation = presentations.find((option) => option.id === line.presentationId);
    const packages = parseDecimalInput(line.packages.trim(), 4);
    if (presentation && packages) parts.push(`${formatWasteQuantity(String(packages), '').trim()} × ${presentation.name}`);
  }
  if (!parts.length) return null;
  const loose = parseDecimalInput(draft.loose.trim(), 4);
  if (loose) parts.push(formatWasteQuantity(String(loose), unitSymbol));
  const { total } = countTotal(draft, presentations);
  return total === null ? null : `${parts.join(' + ')} = ${formatWasteQuantity(String(total), unitSymbol)}`;
}

const money = (value: string | number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value));

export type CountTone = 'ok' | 'warn' | 'bad';

/**
 * La diferencia en palabras, SOLO después de escribir lo contado:
 * «El sistema decía 325 pz → faltan 7 pz (≈ $35.00 · 2.2 %)», «sobran…» o «Cuadra ✓».
 */
export function describeCountDifference(preview: Pick<CountPreview, 'systemQuantity' | 'difference' | 'direction' | 'value' | 'percentOfSystem' | 'large' | 'unit'>): { tone: CountTone; headline: string; detail: string } {
  const symbol = preview.unit.symbol;
  const system = `El sistema decía ${formatWasteQuantity(preview.systemQuantity, symbol)}`;
  if (preview.direction === 'MATCH') return { tone: 'ok', headline: 'Cuadra ✓', detail: `${system}. No se registra nada.` };
  const magnitude = formatWasteQuantity(String(Math.abs(Number(preview.difference))), symbol);
  const extras = [preview.value !== null ? `≈ ${money(preview.value)}` : 'sin costo registrado', preview.percentOfSystem !== null ? `${preview.percentOfSystem.toLocaleString('es-MX', { maximumFractionDigits: 1 })} % de lo que decía` : null]
    .filter(Boolean)
    .join(' · ');
  const verb = preview.direction === 'SHORTAGE' ? 'Faltan' : 'Sobran';
  return { tone: preview.large ? 'bad' : 'warn', headline: `${verb} ${magnitude}`, detail: `${system} → ${verb.toLowerCase()} ${magnitude} (${extras}).` };
}

/** La parte del faltante que el gerente reconoce como merma: vacío = ninguna; nunca más de lo que faltó. */
export function detectedWasteProblem(text: string, shortage: number): { quantity: number | null; problem: string | null } {
  const trimmed = text.trim();
  if (!trimmed) return { quantity: null, problem: null };
  const read = readCountNumber(trimmed);
  if (read.problem) return { quantity: null, problem: read.problem };
  const quantity = read.value ?? 0;
  if (quantity <= 0) return { quantity: null, problem: null };
  if (quantity > shortage) return { quantity: null, problem: 'La merma no puede ser mayor que lo que faltó.' };
  return { quantity, problem: null };
}

export interface CountCompareInputs {
  wasteText: string;
  reason: string;
  syncAcknowledged: boolean;
  /** Confirmó el aviso de compras o transferencias que todavía no suman. */
  supplyAcknowledged?: boolean;
}

/** «El ticket más antiguo es del 10 sep 2026.»: para elegir el rango en «Descuento por ventas». */
export function pendingOldestText(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `El ticket más antiguo es del ${date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}.`;
}

/**
 * Qué muestra y qué exige el paso «Revisa la diferencia», derivado SOLO de la comparación vigente.
 * Así un dato que ya no aplica (una merma escrita para un faltante que se volvió sobrante) nunca
 * bloquea un botón cuyo campo no está a la vista.
 */
export function countCompareState(preview: CountPreview | undefined, inputs: CountCompareInputs) {
  const none = { quantity: null, problem: null } as { quantity: number | null; problem: string | null };
  if (!preview) return { shortage: 0, waste: none, blockedMessage: null as string | null, cannotApplySurplus: false, needsReason: false, needsSyncAck: false, needsSupplyAck: false, blocker: null as string | null };
  const blockedMessage = !preview.storeStarted ? 'Carga primero el inventario inicial de esta tienda. Después ya podrás contar normal.'
    : preview.parallelBalance ? 'Este insumo tiene existencia registrada en otra unidad en esta tienda. Pide que se corrija antes de contar.'
      : preview.pending?.blocked ? [preview.pending.message ?? 'Hay ventas sin descontar que usan este insumo.', pendingOldestText(preview.pending.loyverseOldestAt)].filter(Boolean).join(' ')
        : null;
  const actionable = preview.direction !== 'MATCH' && !blockedMessage;
  const shortage = preview.direction === 'SHORTAGE' ? Math.abs(Number(preview.difference)) : 0;
  const waste = actionable && preview.direction === 'SHORTAGE' ? detectedWasteProblem(inputs.wasteText, shortage) : none;
  const cannotApplySurplus = actionable && preview.direction === 'SURPLUS' && !preview.costAvailable;
  const needsReason = actionable && preview.large && !cannotApplySurplus;
  const needsSyncAck = actionable && !cannotApplySurplus && preview.salesSync.stale;
  const needsSupplyAck = actionable && !cannotApplySurplus && Boolean(preview.pendingSupply);
  const blocker = blockedMessage ? 'Primero resuelve lo que se indica arriba.'
    : cannotApplySurplus ? 'Sin costo registrado no se puede agregar el sobrante.'
      : waste.problem
        ?? (needsReason ? largeReasonProblem(true, inputs.reason) : null)
        ?? (needsSyncAck && !inputs.syncAcknowledged ? 'Confirma que quieres contar aunque Loyverse no esté al día.' : null)
        ?? (needsSupplyAck && !inputs.supplyAcknowledged ? 'Confirma lo de las compras o transferencias pendientes.' : null);
  return { shortage, waste, blockedMessage, cannotApplySurplus, needsReason, needsSyncAck, needsSupplyAck, blocker };
}

/**
 * Rechazos del servidor que cambian lo que la pantalla debe pedir (existencia nueva, motivo, aviso de
 * Loyverse, ventas pendientes, costo...): se vuelve a comparar conservando lo escrito, y la pantalla
 * muestra el campo o el aviso que hace falta. En ninguno de ellos se escribió nada.
 */
const RECOMPARE_CODES = new Set(['COUNT_STOCK_CHANGED', 'COUNT_SALES_SYNC_STALE', 'COUNT_REASON_REQUIRED', 'COUNT_PENDING_SALES', 'COUNT_COST_REQUIRED', 'COUNT_PARALLEL_BALANCE', 'COUNT_STORE_NOT_STARTED', 'COUNT_PENDING_SUPPLY']);
export function countErrorNeedsRecompare(code: string | undefined): boolean {
  return Boolean(code && RECOMPARE_CODES.has(code));
}

/** Motivo de una diferencia grande: el servidor exige al menos 10 caracteres. */
export function largeReasonProblem(large: boolean, reason: string): string | null {
  if (!large) return null;
  return reason.trim().length >= 10 ? null : 'La diferencia es grande: escribe por qué (al menos 10 caracteres).';
}

/** Lo que se manda. SIN fecha, costo, tipo ni unidad; `seenQuantity` = lo que decía el sistema al mostrarse la diferencia. */
export function buildCountPayload(input: {
  requestId: string;
  storeId: string;
  ingredientId: string;
  countedQuantity: number;
  seenQuantity: string;
  detectedWasteQuantity: number | null;
  largeDifferenceReason: string;
  large: boolean;
  staleSalesSyncAcknowledged: boolean;
  pendingSupplyAcknowledged?: boolean;
}): CreateCountInput {
  const reason = input.largeDifferenceReason.trim();
  return {
    requestId: input.requestId,
    storeId: input.storeId,
    ingredientId: input.ingredientId,
    countedQuantity: input.countedQuantity,
    seenQuantity: Number(input.seenQuantity),
    ...(input.detectedWasteQuantity ? { detectedWasteQuantity: input.detectedWasteQuantity } : {}),
    ...(input.large && reason ? { largeDifferenceReason: reason } : {}),
    ...(input.staleSalesSyncAcknowledged ? { staleSalesSyncAcknowledged: true } : {}),
    ...(input.pendingSupplyAcknowledged ? { pendingSupplyAcknowledged: true } : {}),
  };
}

/** El umbral de «diferencia grande» en palabras (distinto del de merma grande). */
export function countThresholdSummary(threshold: Pick<CountThreshold, 'mode' | 'value' | 'uncostedPercentage' | 'isDefault'>): string {
  const percent = (value: number) => `${value.toLocaleString('es-MX', { maximumFractionDigits: 2 })} %`;
  const rule = threshold.mode === 'AMOUNT'
    ? `Al contar, una diferencia es grande desde ${money(threshold.value)} (si el insumo no tiene costo, desde ${percent(threshold.uncostedPercentage)} de lo que decía el sistema)`
    : `Al contar, una diferencia es grande desde ${percent(threshold.value)} de lo que decía el sistema`;
  return `${rule}; entonces se pide un motivo.${threshold.isDefault ? ' Es la sugerencia inicial; el dueño puede cambiarla.' : ''}`;
}

/** «hace 2 h» / «hace 25 min» / «nunca»: para el aviso de Loyverse. */
export function sinceText(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'nunca';
  const minutes = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `hace ${hours} h`;
  return `hace ${Math.round(hours / 24)} días`;
}
